/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { VError } from 'verror';
import { ActionButton, Popover } from '@blend/enterprise-components';
import { notaryAPI } from '../../../../api/notary';
import { usePermissions } from '../../../../../src/hooks/usePermissions';
import { NotaryAPI } from '../../../../../src/api/notary/types';
import { RoomsPipelineFilterContent } from './RoomsPipelineFilterContent';
import { diagnostics } from '@utils/diagnostics';

export namespace RoomsPipelineFilter {
  export interface Props {
    getAssignedRooms?: boolean;
  }
}

export const RoomsPipelineFilter: React.FC<RoomsPipelineFilter.Props> = ({ getAssignedRooms = false }) => {
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const [hasLoadedInformation, setHasLoadedInformation] = useState(false);
  const hasSuperAdminPermission = usePermissions().includes(NotaryAPI.BSRPermissions.BSRSuperAdmin);

  const [allOrganizations, setAllOrganizations] = useState<string[]>([]);
  const [allGroups, setAllGroups] = useState<string[]>([]);

  useEffect(() => {
    const getAllOrganizations = async () => {
      try {
        const { organizations } = getAssignedRooms
          ? await notaryAPI.rooms.getAssignedOrganizations()
          : await notaryAPI.rooms.getAllOrganizations();

        setAllOrganizations(organizations);
      } catch (e) {
        const err = e as Error;
        const wrappedErr = new VError(err, 'Failed to get organizations for the rooms filter');
        diagnostics.error(wrappedErr, { getAssignedRooms });
      }
    };

    const getAllGroups = async () => {
      try {
        const { groups } = await notaryAPI.rooms.getAllGroups();

        const names = groups.map(group => group.name);
        setAllGroups(names);
      } catch (e) {
        const err = e as Error;
        const wrappedErr = new VError(err, 'Failed to get groups for the rooms filter');
        diagnostics.error(wrappedErr);
      }
    };

    if (!hasLoadedInformation) {
      getAllOrganizations();
      if (hasSuperAdminPermission) {
        getAllGroups();
      }
      setHasLoadedInformation(true);
    }
  }, [hasLoadedInformation, getAssignedRooms]);

  const closeFilterPopover = () => {
    setIsFilterPopoverOpen(false);
  };

  const toggleFilterPopover = () => {
    setIsFilterPopoverOpen(!isFilterPopoverOpen);
  };

  return (
    <Popover
      anchorPosition="bottom-end"
      isOpen={isFilterPopoverOpen}
      onClose={closeFilterPopover}
      popoverContent={
        <RoomsPipelineFilterContent
          closePopup={closeFilterPopover}
          allOrganizations={allOrganizations}
          allGroups={allGroups}
        />
      }
    >
      <ActionButton
        data-dd-privacy="allow"
        icon="filter_list"
        id="it-PipelineFilter-addFilters-btn"
        label="Filter"
        onClick={toggleFilterPopover}
        theme="tertiary"
      />
    </Popover>
  );
};
